import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import CarHeroCarousel from '@/components/ui/car-hero-carousel';
import type { Car } from '@/contexts/CarContext';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/i18n/LanguageContext';

interface CarHeroCarouselDeckProps {
  cars: Car[];
  activeCarId?: string;
  onSelect: (carId: string) => void;
  onEdit: (carId: string) => void;
  onDelete: (carId: string) => void;
}

const CarHeroCarouselDeck: React.FC<CarHeroCarouselDeckProps> = ({ cars, activeCarId, onSelect, onEdit, onDelete }) => {
  const [api, setApi] = useState<CarouselApi | undefined>();
  const [index, setIndex] = useState<number>(0);
  const navigate = useNavigate();
  const { t } = useI18n();

  const idToIndex = useMemo(() => {
    const map = new Map<string, number>();
    cars.forEach((c, i) => map.set(c.id, i));
    return map;
  }, [cars]);

  useEffect(() => {
    if (activeCarId && idToIndex.has(activeCarId)) {
      const target = idToIndex.get(activeCarId)!;
      setIndex(target);
      if (api) {
        api.scrollTo(target);
      }
    }
  }, [activeCarId, idToIndex, api]);

  useEffect(() => {
    if (!api) return;
    const onSelectHandler = () => {
      const newIdx = api.selectedScrollSnap();
      setIndex(newIdx);
      const car = cars[newIdx];
      if (car) onSelect(car.id);
    };
    api.on('select', onSelectHandler);
    return () => {
      api.off('select', onSelectHandler);
    };
  }, [api, cars, onSelect]);

  // Show empty state card when no cars (no carousel wrapper)
  if (cars.length === 0) {
    return (
      <div className="w-full px-2 sm:px-4">
        <div 
          className="bg-white rounded-2xl p-5 w-full text-center"
          style={{
            boxShadow: '0px 10px 8px -1px #EEEEEE'
          }}
        >
          {/* Icon */}
          <div className="flex justify-center mb-2">
            <img 
              src="/icons/CarDetailPage/NoCar.png" 
              alt="No car" 
              className="w-20 h-20 object-contain"
            />
          </div>

          {/* Title */}
          <h2 
            className="text-lg font-semibold text-gray-900 mb-3"
            style={{ fontFamily: 'Lato' }}
          >
            {t('cars.noCars') || 'No cars added yet'}
          </h2>

          {/* Description */}
          <p 
            className="text-gray-500 leading-relaxed px-2 mb-4"
            style={{ fontFamily: 'Lato', fontSize: '0.8rem' }}
          >
            {t('cars.emptyStateDescription') || 'Add your first car to unlock full tracking features and keep your vehicle in top shape.'}
          </p>

          {/* CTA Button */}
          <Button
            onClick={() => navigate('/cars/add')}
            className="w-full py-2.5 rounded-xl bg-[#e7fe52] text-gray-900 font-semibold text-sm hover:opacity-90 flex items-center justify-center"
            style={{ fontFamily: 'Lato', boxShadow: 'none' }}
          >
            <Plus size={18} className="mr-2" />
            {t('cars.addCar') || 'Add Car'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="w-full overflow-hidden rounded-[10px] bg-[#FDFDFD]" style={{ height: '30vh' }}>
        <Carousel opts={{ align: 'start', loop: false }} className="w-full h-full" setApi={setApi}>
          <CarouselContent className="h-full">
            {cars.map((car) => (
              <CarouselItem key={car.id} className="h-full">
                <CarHeroCarousel
                  car={car}
                  onEdit={() => onEdit(car.id)}
                  onDelete={() => onDelete(car.id)}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

      {cars.length > 1 && (
        <div className="mt-2 flex justify-center gap-1 px-2">
          {cars.map((car, i) => (
            <button
              key={car.id}
              type="button"
              aria-label={`Select car ${i + 1}`}
              aria-current={i === index}
              className="focus:outline-none"
              onClick={() => {
                setIndex(i);
                if (api) api.scrollTo(i);
                onSelect(car.id);
              }}
            >
              <div
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === index ? 'bg-[#e7fe52] w-6' : 'bg-gray-300 w-3'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CarHeroCarouselDeck;


